import {
	Avatar,
	Badge,
	Button,
	Card,
	Divider,
	Flex,
	Group,
	Modal,
	Popover,
	Select,
	SimpleGrid,
	Stack,
	Tabs,
	Text,
	TextInput,
} from '@mantine/core'
import {
	IconBriefcase,
	IconBus,
	IconCar,
	IconChevronDown,
	IconCompass,
	IconCopy,
	IconEye,
	IconEyeOff,
	IconFileSearch,
	IconLayoutGrid,
	IconMapPin,
	IconRefresh,
	IconRestore,
	IconSearch,
	IconStarFilled,
	IconTrash,
	IconUsers,
	IconX,
} from '@tabler/icons-react'
import dayjs from 'dayjs'
import { MRT_ColumnDef, MRT_PaginationState } from 'mantine-react-table'
import { forwardRef, ReactNode } from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
	deleteFindMyGuideCmsListing,
	getFindMyGuideCmsCategories,
	getFindMyGuideCmsDeletedProfiles,
	getFindMyGuideCmsListingDetail,
	getFindMyGuideCmsListings,
	getFindMyGuideCmsStatistics,
	hideFindMyGuideCmsListing,
	restoreFindMyGuideCmsDeletedProfile,
} from 'src/apis/findMyGuideAPI'
import CustomTooltip from 'src/components/CustomComponents/CustomTooltip'
import TableComponent from 'src/components/TableComponent'
import actionClasses from 'src/components/TableComponent/TableComponent.module.scss'
import UserIdentityChip from 'src/components/UserIdentityChip'
import { useModal } from 'src/context/ModalContext'
import { useQueryParamTab } from 'src/utils/tabQueryParam'
import styles from './FindMyGuidePage.module.scss'

interface GuideStats {
	totalListings: number
	active: number
	sellers: number
	buyers: number
	tours: number
	localGuides: number
	privateTransports: number
	publicTransports: number
}

interface GuideCategory {
	id: string
	title: string
	amount: number
	image?: string
}

interface DeletedGuideProfile {
	id: string
	name: string
	avatar: string
	deletedAt: string
	owner: {
		id: string
		name: string
		email: string
		phone: string
	}
}

interface GuideListing {
	id: string
	no: number
	image: string
	title: string
	description: string
	seller?: {
		id: string
		name: string
		avatar: string
		email: string
	}
	serviceType: string
	serviceTypeLabel: string
	category: string
	location: string
	price: number
	rating: number
	ratingCount: number
	status: string
	isShowByAdmin: boolean
	isHidden: boolean
	shareLink: string
	createdAt: string
	updatedAt: string
	detail?: {
		languages?: any[]
		schedule?: any
		hotel_pickup?: boolean
		meeting_point_pickup?: boolean
		pickup_details?: any[]
		itinerary?: any[]
		max_participants?: number
		spots_available?: number
		categories?: any[]
		what_to_bring?: string
		what_included?: string
		child_pet_policy?: string
		insurance?: boolean
		insurance_note?: string
		business_license?: boolean
		business_license_note?: string
		business_location?: any
		cancel_policy_note?: string
		cancel_policies?: any[]
		price_details?: any[]
	}
}

const SERVICE_TYPE_OPTIONS = [
	{ label: 'All Services', value: '' },
	{ label: 'Tour', value: 'tour' },
	{ label: 'Local Guide', value: 'local_guide' },
	{ label: 'Private Bus', value: 'private_trans' },
	{ label: 'Public Bus', value: 'public_trans' },
]

const STATUS_OPTIONS = [
	{ label: 'All', value: '' },
	{ label: 'Active', value: 'true' },
	{ label: 'Inactive', value: 'false' },
]

const SERVICE_TYPE_META: Record<string, any> = {
	'': { label: 'All Services', icon: IconLayoutGrid },
	tour: { label: 'Tour', icon: IconCompass },
	local_guide: { label: 'Local Guide', icon: IconUsers },
	private_trans: { label: 'Private Bus', icon: IconCar },
	public_trans: { label: 'Public Bus', icon: IconBus },
}

const getServiceTypeMeta = (value?: string, fallbackLabel = '-') => {
	const meta = SERVICE_TYPE_META[value || '']
	return meta || { label: fallbackLabel, icon: IconCompass }
}

const ServiceTypeSelectItem = forwardRef<HTMLDivElement, any>(
	({ label, value, ...others }, ref) => {
		const meta = getServiceTypeMeta(value, label)
		const Icon = meta.icon

		return (
			<div ref={ref} {...others} className={`${styles.serviceTypeOption} ${others.className || ''}`}>
				<span className={styles.serviceTypeOptionIcon}><Icon size={17} /></span>
				<span>{label}</span>
			</div>
		)
	},
)

ServiceTypeSelectItem.displayName = 'ServiceTypeSelectItem'

const SORT_OPTIONS = [
	{ label: 'Tour date: soonest', value: 'tour-date-soonest' },
	{ label: 'Tour date: lasted', value: 'tour-date-lasted' },
	{ label: 'Created at: newest', value: 'created_at desc' },
	{ label: 'Updated at', value: 'updated_at desc' },
	{ label: 'Rating: High to Low', value: 'rating-high' },
	{ label: 'Rating: Low to High', value: 'rating-low' },
]

const DEFAULT_SORT_VALUE = 'created_at desc'

const FILTER_SELECT_PROPS = {
	withinPortal: true,
	zIndex: 1000,
	maxDropdownHeight: 260,
}

const formatDate = (value?: string) => {
	const parsed = dayjs(value)
	return parsed.isValid() ? parsed.format('DD/MM/YYYY') : '-'
}

const formatVndPrice = (value?: number) => `${new Intl.NumberFormat('vi-VN').format(Number(value || 0))}đ`

const hasValue = (value: any) => {
	if (Array.isArray(value)) return value.length > 0
	return value !== null && value !== undefined && value !== ''
}

const formatBooleanAvailability = (value?: boolean) => (value ? 'Available' : 'Not available')

const joinText = (items: string[]) => items.filter(Boolean).join(', ')

const getLocationAddress = (location?: any) => location?.address || ''

const formatSchedule = (schedule?: any) => {
	if (!schedule) return ''
	const type = schedule.schedule_type || ''
	const dateRange = [schedule.start_date, schedule.end_date].filter(Boolean).join(' to ')
	return [type, dateRange].filter(Boolean).join(' | ')
}

const getRowsAndCount = (response: any) => {
	const objects = response?.data?.results?.objects
	const rows = Array.isArray(objects?.rows) ? objects.rows : []
	const count = Number(objects?.count ?? response?.data?.pagination?.total ?? rows.length)

	return { rows, count }
}

const mapStats = (response: any): GuideStats => {
	const item = response?.data?.results?.object || {}

	return {
		totalListings: Number(item.total_listings || 0),
		active: Number(item.active || 0),
		sellers: Number(item.sellers || 0),
		buyers: Number(item.buyers || 0),
		tours: Number(item.tours || 0),
		localGuides: Number(item.local_guides || 0),
		privateTransports: Number(item.private_transports || 0),
		publicTransports: Number(item.public_transports || 0),
	}
}

const normalizeListing = (
	item: any,
	index: number,
	pagination: MRT_PaginationState,
): GuideListing => ({
	id: String(item?.id || ''),
	no: index + 1 + pagination.pageSize * (pagination.pageIndex - 1),
	image: String(item?.image || ''),
	title: String(item?.title || item?.name || '-'),
	description: String(item?.description || ''),
	seller: item?.seller
		? {
				id: String(item.seller.id || ''),
				name: String(item.seller.name || ''),
				avatar: String(item.seller.avatar || ''),
				email: String(item.seller.email || ''),
		  }
		: undefined,
	serviceType: String(item?.service_type || ''),
	serviceTypeLabel: String(item?.service_type_label || item?.service_type || ''),
	category: String(item?.category || item?.categories?.[0]?.title || ''),
	location: String(item?.location || ''),
	price: Number(item?.price || 0),
	rating: Number(item?.rating || 0),
	ratingCount: Number(item?.rating_count || 0),
	status: String(item?.status || ''),
	isShowByAdmin:
		typeof item?.is_show_by_admin === 'boolean'
			? item.is_show_by_admin
			: !Boolean(item?.is_hidden || item?.status === 'inactive'),
	isHidden:
		typeof item?.is_show_by_admin === 'boolean'
			? !item.is_show_by_admin
			: Boolean(item?.is_hidden || item?.status === 'inactive'),
	shareLink: String(item?.share_link || ''),
	createdAt: formatDate(item?.created_at),
	updatedAt: formatDate(item?.updated_at),
	detail: item?.detail || {},
})

const TooltipText = ({
	value,
	lineClamp = 1,
	className,
}: {
	value?: string
	lineClamp?: number
	className?: string
}) => (
	<CustomTooltip label={value || '-'} position="top" withArrow maxWidth={520} offset={8}>
		<Text size="sm" color="#344054" lineClamp={lineClamp} className={className}>
			{value || '-'}
		</Text>
	</CustomTooltip>
)

const DetailSection = ({
	title,
	children,
}: {
	title: string
	children: ReactNode
}) => (
	<Card withBorder radius="md" p="md" className={styles.detailSection}>
		<Stack spacing={10}>
			<Text fw={800}>{title}</Text>
			{children}
		</Stack>
	</Card>
)

const DetailRow = ({
	label,
	value,
}: {
	label: string
	value?: ReactNode
}) => {
	if (!hasValue(value)) return null

	return (
		<Group position="apart" align="flex-start" spacing={12} noWrap className={styles.detailRow}>
			<Text size="sm" color="dimmed" fw={700}>{label}</Text>
			<Text size="sm" align="right" className={styles.detailRowValue}>{value}</Text>
		</Group>
	)
}

function FindMyGuidePage() {
	const { openError, openModal } = useModal()
	const tabValues = ['listings', 'categories', 'deleted-profiles'] as const
	const { activeTab, handleTabChange } = useQueryParamTab(tabValues, 'listings')
	const [, setSearchParams] = useSearchParams()
	const [rows, setRows] = useState<GuideListing[]>([])
	const [categories, setCategories] = useState<GuideCategory[]>([])
	const [categoryOptions, setCategoryOptions] = useState<GuideCategory[]>([])
	const [deletedProfiles, setDeletedProfiles] = useState<DeletedGuideProfile[]>([])
	const [stats, setStats] = useState<GuideStats>({
		totalListings: 0,
		active: 0,
		sellers: 0,
		buyers: 0,
		tours: 0,
		localGuides: 0,
		privateTransports: 0,
		publicTransports: 0,
	})
	const [loading, setLoading] = useState(false)
	const [actionLoadingId, setActionLoadingId] = useState<string | null>(null)
	const [selectedListing, setSelectedListing] = useState<GuideListing | null>(null)
	const [searchKey, setSearchKey] = useState('')
	const [categorySearch, setCategorySearch] = useState('')
	const [deletedProfileSearch, setDeletedProfileSearch] = useState('')
	const [serviceType, setServiceType] = useState('')
	const [statusFilter, setStatusFilter] = useState('')
	const [categoryIds, setCategoryIds] = useState<string[]>([])
	const [sortValue, setSortValue] = useState(DEFAULT_SORT_VALUE)
	const [total, setTotal] = useState(0)
	const [categoryTotal, setCategoryTotal] = useState(0)
	const [categoryOptionsTotal, setCategoryOptionsTotal] = useState(0)
	const [pagination, setPagination] = useState<MRT_PaginationState>({
		pageIndex: 1,
		pageSize: 10,
	})
	const [categoryPagination, setCategoryPagination] = useState<MRT_PaginationState>({
		pageIndex: 1,
		pageSize: 10,
	})
	const [deletedProfilePagination, setDeletedProfilePagination] = useState<MRT_PaginationState>({
		pageIndex: 1,
		pageSize: 10,
	})
	const [deletedProfileTotal, setDeletedProfileTotal] = useState(0)

	const fetchStats = useCallback(async () => {
		const response = await getFindMyGuideCmsStatistics()
		setStats(mapStats(response))
	}, [])

	const fetchCategories = useCallback(async () => {
		const response = await getFindMyGuideCmsCategories({
			page: categoryPagination.pageIndex,
			limit: categoryPagination.pageSize,
			q: categorySearch || undefined,
		})
		const { rows: apiRows, count } = getRowsAndCount(response)

		setCategories(
			apiRows.map((item: any) => ({
				id: String(item?.id || item?.category_id || ''),
				title: String(item?.title || ''),
				image: String(item?.image || ''),
				amount: Number(item?.amount || 0),
			})),
		)
		setCategoryTotal(count)
	}, [categoryPagination, categorySearch])

	const fetchCategoryOptions = useCallback(async () => {
		const response = await getFindMyGuideCmsCategories({
			page: 1,
			limit: 1000,
		})
		const { rows: apiRows, count } = getRowsAndCount(response)

		setCategoryOptions(
			apiRows.map((item: any) => ({
				id: String(item?.id || item?.category_id || ''),
				title: String(item?.title || ''),
				image: String(item?.image || ''),
				amount: Number(item?.amount || 0),
			})),
		)
		setCategoryOptionsTotal(count)
	}, [])

	const fetchDeletedProfiles = useCallback(async () => {
		const response = await getFindMyGuideCmsDeletedProfiles({
			page: deletedProfilePagination.pageIndex,
			limit: deletedProfilePagination.pageSize,
			q: deletedProfileSearch || undefined,
		})
		const { rows: apiRows, count } = getRowsAndCount(response)
		setDeletedProfiles(apiRows.map((item: any) => ({
			id: String(item?.id || ''),
			name: String(item?.business_full_name || item?.full_name || '-'),
			avatar: String(item?.avatar || ''),
			deletedAt: formatDate(item?.deleted_at),
			owner: {
				id: String(item?.user?.id || item?.user_id || ''),
				name: String(item?.user?.name || '-'),
				email: String(item?.user?.email || '-'),
				phone: String(item?.user?.phone || '-'),
			},
		})))
		setDeletedProfileTotal(count)
	}, [deletedProfilePagination, deletedProfileSearch])

	const fetchListings = useCallback(async () => {
		setLoading(true)

		try {
			const response = await getFindMyGuideCmsListings({
				page: pagination.pageIndex,
				limit: pagination.pageSize,
				q: searchKey || undefined,
				sort: sortValue,
				service_type: serviceType || undefined,
				is_show_by_admin:
					statusFilter === '' ? undefined : statusFilter === 'true',
				status:
					statusFilter === 'true'
						? 'visible'
						: statusFilter === 'false'
							? 'hidden'
							: undefined,
				category_id: categoryIds.length ? categoryIds.join(',') : undefined,
			})
			const { rows: apiRows, count } = getRowsAndCount(response)

			setRows(apiRows.map((item: any, index: number) => normalizeListing(item, index, pagination)))
			setTotal(count)
		} catch (error: any) {
			setRows([])
			setTotal(0)
			toast.error(error?.response?.data?.message || error?.message || 'Failed to load FMG listings')
		} finally {
			setLoading(false)
		}
	}, [categoryIds, pagination, searchKey, serviceType, sortValue, statusFilter])

	const refreshPage = useCallback(async () => {
		await Promise.all([
			fetchStats(),
			fetchListings(),
			fetchCategories(),
			fetchCategoryOptions(),
			fetchDeletedProfiles(),
		])
	}, [fetchCategories, fetchCategoryOptions, fetchDeletedProfiles, fetchListings, fetchStats])

	useEffect(() => {
		refreshPage()
	}, [refreshPage])

	const resetPage = useCallback(() => {
		setPagination((previous) => ({ ...previous, pageIndex: 1 }))
	}, [])

	const handleCopy = useCallback(async (value?: string, label = 'Copied') => {
		if (!value || !navigator?.clipboard?.writeText) return
		await navigator.clipboard.writeText(value)
		toast.success(label)
	}, [])

	const handleView = useCallback(async (row: GuideListing) => {
		setActionLoadingId(row.id)
		try {
			const response = await getFindMyGuideCmsListingDetail(row.id)
			setSelectedListing(normalizeListing(response?.data?.results?.object, row.no - 1, pagination))
		} finally {
			setActionLoadingId(null)
		}
	}, [pagination])

	const handleHide = useCallback(async (row: GuideListing) => {
		setActionLoadingId(row.id)
		try {
			await hideFindMyGuideCmsListing(row.id, !row.isHidden)
			await refreshPage()
		} finally {
			setActionLoadingId(null)
		}
	}, [refreshPage])

	const handleDelete = useCallback(async (row: GuideListing) => {
		if (!window.confirm(`Delete "${row.title}"?`)) return

		setActionLoadingId(row.id)
		try {
			await deleteFindMyGuideCmsListing(row.id)
			toast.success('Listing deleted', { autoClose: 5000 })
			await refreshPage()
		} finally {
			setActionLoadingId(null)
		}
	}, [refreshPage])

	const restoreDeletedProfile = useCallback(async (profile: DeletedGuideProfile) => {
		setActionLoadingId(profile.id)
		try {
			await restoreFindMyGuideCmsDeletedProfile(profile.id)
			toast.success('FMG profile restored')
			await refreshPage()
		} catch (error: any) {
			openError(error)
		} finally {
			setActionLoadingId(null)
		}
	}, [openError, refreshPage])

	const handleRestoreDeletedProfile = useCallback((profile: DeletedGuideProfile) => {
		openModal({
			type: 'confirm',
			label: (
				<div>
					Restore deleted FMG profile <strong>{profile.name}</strong> for{' '}
					<strong>{profile.owner.phone}</strong>?
				</div>
			),
			handleConfirm: () => restoreDeletedProfile(profile),
		})
	}, [openModal, restoreDeletedProfile])

	const handleToggleCategory = useCallback((categoryId: string) => {
		setCategoryIds((previous) => {
			if (previous.includes(categoryId)) {
				return previous.filter((id) => id !== categoryId)
			}

			return [...previous, categoryId]
		})
		resetPage()
	}, [resetPage])

	const getStatusFilterValue = useCallback((value?: string) => {
		if (!value) return ''
		if (value === 'true' || value === 'visible' || value === 'active') {
			return 'true'
		}
		if (value === 'false' || value === 'hidden' || value === 'inactive') {
			return 'false'
		}
		return ''
	}, [])

	const handleCategoryListingsClick = useCallback((category: GuideCategory) => {
		if (!category.id || category.amount <= 0) return

		setSearchKey('')
		setServiceType('')
		setCategoryIds([category.id])
		setPagination((previous) => ({ ...previous, pageIndex: 1 }))
		setSearchParams((previous) => {
			const next = new URLSearchParams(previous)
			next.set('tab', 'listings')
			next.set('page', '1')
			next.set('category_id', category.id)
			next.delete('search')
			next.delete('search_key')
			next.delete('service_type')
			return next
		}, { replace: true })
	}, [setSearchParams])

	const columns = useMemo<MRT_ColumnDef[]>(
		() => [
			{ accessorKey: 'no', header: 'STT', size: 48, maxSize: 48 },
			{
				accessorKey: 'title',
				header: 'Title',
				size: 230,
				Cell: ({ row }: any) => (
					<Group spacing={10} noWrap>
						<Avatar src={row.original.image || undefined} radius="md" size={50} className={styles.tableImage}>
							{String(row.original.title || '?').charAt(0).toUpperCase()}
						</Avatar>
						<Stack spacing={3} className={styles.nameCell}>
							<TooltipText value={row.original.title} className={styles.titleText} />
							<TooltipText
								value={row.original.description || 'No description'}
								lineClamp={2}
								className={styles.descriptionText}
							/>
							<Group spacing={4} noWrap className={styles.idRow}>
								<Text size="xs" color="dimmed" lineClamp={1} className={styles.idText}>
									{row.original.id}
								</Text>
								<button
									type="button"
									onClick={() => handleCopy(row.original.id, 'Copied ID')}
									className={styles.copyIdBtn}
								>
									<IconCopy size={13} />
								</button>
							</Group>
						</Stack>
					</Group>
				),
			},
			{
				accessorKey: 'seller',
				header: 'Seller',
				size: 210,
				Cell: ({ row }: any) =>
					row.original.seller ? (
						<Stack spacing={2}>
							<UserIdentityChip
								name={row.original.seller.name}
								avatar={row.original.seller.avatar}
								compact
							/>
							<Text size="xs" color="dimmed" lineClamp={1}>
								{row.original.seller.email || '-'}
							</Text>
						</Stack>
					) : (
						<Text size="sm" color="dimmed">-</Text>
					),
			},
			{
				accessorKey: 'serviceTypeLabel',
				header: 'Service Type',
				size: 120,
				Cell: ({ row }: any) => {
					const meta = getServiceTypeMeta(row.original.serviceType, row.original.serviceTypeLabel)
					const Icon = meta.icon

					return (
						<span className={styles.serviceTypeBadge}>
							<Icon size={14} />
							<span>{row.original.serviceTypeLabel || '-'}</span>
						</span>
					)
				},
			},
			{ accessorKey: 'category', header: 'Category', size: 130 },
			{
				accessorKey: 'status',
				header: 'Status by user',
				size: 96,
				maxSize: 96,
				Cell: ({ row }: any) => {
					const status = String(row.original.status || '').toLowerCase()
					const statusMeta: Record<string, { label: string; color: string }> = {
						active: { label: 'Active', color: 'green' },
						inactive: { label: 'Inactive', color: 'gray' },
						draft: { label: 'Draft', color: 'yellow' },
					}
					const meta = statusMeta[status]

					return meta ? (
						<Badge color={meta.color} variant="light" sx={{ textTransform: 'none' }}>
							{meta.label}
						</Badge>
					) : (
						<Text size="sm" color="dimmed">-</Text>
					)
				},
			},
			{
				accessorKey: 'isShowByAdmin',
				header: 'Status by admin',
				size: 110,
				maxSize: 110,
				Cell: ({ row }: any) => (
					<Badge
						color={row.original.isShowByAdmin ? 'green' : 'gray'}
						variant="light"
						sx={{ textTransform: 'none' }}
					>
						{row.original.isShowByAdmin ? 'Active' : 'Inactive'}
					</Badge>
				),
			},
			{
				accessorKey: 'location',
				header: 'Location',
				size: 170,
				Cell: ({ row }: any) => (
					<Group spacing={4} noWrap className={styles.locationCell}>
						<IconMapPin size={14} color="#667085" />
						<TooltipText value={row.original.location} />
					</Group>
				),
			},
			{
				accessorKey: 'price',
				header: 'Price',
				size: 82,
				Cell: ({ row }: any) => (
					<Text size="sm" fw={700}>{formatVndPrice(row.original.price)}</Text>
				),
			},
			{
				accessorKey: 'rating',
				header: 'Rating',
				size: 86,
				Cell: ({ row }: any) => (
					<Group spacing={4} noWrap>
						<IconStarFilled size={14} color="#f7b500" />
						<Text size="sm">{row.original.rating || '-'} ({row.original.ratingCount})</Text>
					</Group>
				),
			},
			{ accessorKey: 'createdAt', header: 'Created at', size: 105 },
			{ accessorKey: 'updatedAt', header: 'Updated at', size: 105 },
		],
		[handleCopy],
	)

	const categoryColumns = useMemo<MRT_ColumnDef[]>(
		() => [
			{
				accessorKey: 'image',
				header: 'Image',
				size: 80,
				maxSize: 80,
				mantineTableHeadCellProps: {
					sx: { paddingLeft: '16px !important' },
				},
				mantineTableBodyCellProps: {
					sx: { paddingLeft: '16px !important' },
				},
				Cell: ({ row }: any) => (
					<Avatar
						src={row.original.image || undefined}
						radius="md"
						size={50}
						className={styles.tableImage}
					>
						{String(row.original.title || '?').charAt(0).toUpperCase()}
					</Avatar>
				),
			},
			{
				accessorKey: 'title',
				header: 'Category',
				mantineTableHeadCellProps: {
					sx: { paddingLeft: '16px !important' },
				},
				mantineTableBodyCellProps: {
					sx: { paddingLeft: '16px !important' },
				},
				Cell: ({ row }: any) => (
					<button
						type="button"
						className={styles.categoryAmountButton}
						disabled={!row.original.id || row.original.amount <= 0}
						onClick={() => handleCategoryListingsClick(row.original)}
					>
						{row.original.title}
					</button>
				),
			},
			{
				accessorKey: 'amount',
				header: 'Listings',
				size: 120,
				Cell: ({ row }: any) => (
					<button
						type="button"
						className={styles.categoryAmountButton}
						disabled={!row.original.id || row.original.amount <= 0}
						onClick={() => handleCategoryListingsClick(row.original)}
					>
						{row.original.amount}
					</button>
				),
			},
		],
		[handleCategoryListingsClick],
	)

	const deletedProfileColumns = useMemo<MRT_ColumnDef[]>(
		() => [
			{
				accessorKey: 'name',
				header: 'Profile',
				Cell: ({ row }: any) => (
					<Group spacing={10} noWrap>
						<Avatar src={row.original.avatar || undefined} radius="md" size={42}>
							{String(row.original.name || '?').charAt(0).toUpperCase()}
						</Avatar>
						<Stack spacing={2}>
							<Text size="sm" fw={700}>{row.original.name}</Text>
							<Text size="xs" color="dimmed">{row.original.id}</Text>
						</Stack>
					</Group>
				),
			},
			{
				accessorKey: 'owner',
				header: 'Owner',
				Cell: ({ row }: any) => (
					<Stack spacing={2}>
						<Text size="sm" fw={600}>{row.original.owner.name}</Text>
						<Text size="xs" color="dimmed">{row.original.owner.email}</Text>
						<Text size="xs" color="dimmed">{row.original.owner.phone}</Text>
					</Stack>
				),
			},
			{ accessorKey: 'deletedAt', header: 'Deleted at', size: 150 },
		],
		[],
	)

	const renderCustomActions = useCallback(
		(row: any) => {
			const original = row?.original as GuideListing
			const actionDisabled = Boolean(actionLoadingId)
			const rowLoading = actionLoadingId === original.id

			return (
				<Flex gap={6} align="center">
					<CustomTooltip label="View detail">
						<button
							type="button"
							onClick={() => handleView(original)}
							disabled={actionDisabled}
							className={`${actionClasses.actionButton} ${styles.greenAction} ${rowLoading ? styles.actionLoading : ''}`}
						>
							<IconFileSearch size={16} />
						</button>
					</CustomTooltip>
					<CustomTooltip label={original.isHidden ? 'Set admin status active' : 'Set admin status inactive'}>
						<button
							type="button"
							onClick={() => handleHide(original)}
							disabled={actionDisabled}
							className={`${actionClasses.actionButton} ${styles.greenAction} ${rowLoading ? styles.actionLoading : ''}`}
						>
							{original.isHidden ? <IconEyeOff size={16} /> : <IconEye size={16} />}
						</button>
					</CustomTooltip>
					<CustomTooltip label="Delete listing">
						<button
							type="button"
							onClick={() => handleDelete(original)}
							disabled={actionDisabled}
							className={`${actionClasses.actionButton} ${actionClasses.dangerAction} ${rowLoading ? styles.actionLoading : ''}`}
						>
							<IconTrash size={16} />
						</button>
					</CustomTooltip>
				</Flex>
			)
		},
		[actionLoadingId, handleDelete, handleHide, handleView],
	)

	const overviewStatItems = useMemo(
		() => [
			{ label: 'Total Listings', value: stats.totalListings, icon: IconBriefcase },
			{ label: 'Active', value: stats.active, icon: IconEye },
			{ label: 'Sellers', value: stats.sellers, icon: IconUsers },
			{ label: 'Buyers', value: stats.buyers, icon: IconUsers },
		],
		[stats],
	)

	const serviceStatItems = useMemo(
		() => [
			{ label: 'Tours', value: stats.tours, icon: SERVICE_TYPE_META.tour.icon },
			{ label: 'Local Guides', value: stats.localGuides, icon: SERVICE_TYPE_META.local_guide.icon },
			{ label: 'Private Bus', value: stats.privateTransports, icon: SERVICE_TYPE_META.private_trans.icon },
			{ label: 'Public Bus', value: stats.publicTransports, icon: SERVICE_TYPE_META.public_trans.icon },
		],
		[stats],
	)

	return (
		<Stack className={styles.page} spacing={16}>
			<Tabs value={activeTab} onTabChange={handleTabChange} keepMounted={false}>
				<Tabs.List>
					<Tabs.Tab value="listings" fw={700}>Listings</Tabs.Tab>
					<Tabs.Tab value="categories" fw={700}>Categories</Tabs.Tab>
					<Tabs.Tab value="deleted-profiles" fw={700}>Deleted Profiles</Tabs.Tab>
				</Tabs.List>

				<Tabs.Panel value="listings" pt="md">
					<Stack spacing={16}>
						<Card withBorder radius="md" p={0} className={styles.headerCard}>
							<Flex gap={10} align="flex-end" wrap="wrap" className={styles.toolbar}>
								<Stack spacing={6} className={styles.searchControl}>
									<Text size="sm" fw={700}>Search</Text>
									<TextInput
										value={searchKey}
										onChange={(event) => {
											setSearchKey(event.currentTarget.value)
											resetPage()
										}}
										placeholder="Search name, seller or location"
										icon={<IconSearch size={16} />}
									/>
								</Stack>
								<Stack spacing={6} className={styles.filterControl}>
									<Text size="sm" fw={700}>Service Type</Text>
									<Select
										{...FILTER_SELECT_PROPS}
										value={serviceType}
										data={SERVICE_TYPE_OPTIONS}
										itemComponent={ServiceTypeSelectItem}
										icon={(() => {
											const Icon = getServiceTypeMeta(serviceType).icon
											return <Icon size={17} />
										})()}
										classNames={{ item: styles.serviceTypeSelectItem }}
										onChange={(value) => {
											setServiceType(value || '')
											resetPage()
										}}
									/>
								</Stack>
								<Stack spacing={6} className={styles.filterControl}>
									<Text size="sm" fw={700}>Status by admin</Text>
									<Select
										{...FILTER_SELECT_PROPS}
										value={statusFilter}
										data={STATUS_OPTIONS}
										onChange={(value) => {
											setStatusFilter(value || '')
											resetPage()
										}}
									/>
								</Stack>
								<Stack spacing={6} className={`${styles.filterControl} ${styles.categoryFilterControl}`}>
									<Text size="sm" fw={700}>Categories ({categoryOptionsTotal || categoryOptions.length})</Text>
									<Popover withinPortal zIndex={1000} shadow="md" position="bottom-start">
										<Popover.Target>
											<button type="button" className={styles.categoryFilterButton}>
												<span>{categoryIds.length ? `${categoryIds.length} selected` : 'All Categories'}</span>
												{categoryIds.length ? (
													<IconX
														size={16}
														onClick={(event) => {
															event.stopPropagation()
															setCategoryIds([])
															resetPage()
														}}
													/>
												) : (
													<IconChevronDown size={16} />
												)}
											</button>
										</Popover.Target>
										<Popover.Dropdown p={0} className={styles.categoryChecklistDropdown}>
											<button
												type="button"
												className={styles.categoryAllButton}
												onClick={() => {
													setCategoryIds([])
													resetPage()
												}}
											>
												<span>All Categories</span>
												{categoryIds.length ? <IconX size={16} /> : <IconChevronDown size={16} />}
											</button>
											<div className={styles.categoryChecklistBody}>
												{categoryOptions.map((category) => (
													<label key={category.id} className={styles.categoryCheckRow}>
														<input
															type="checkbox"
															checked={categoryIds.includes(category.id)}
															onChange={() => handleToggleCategory(category.id)}
														/>
														<span>{category.title} ({category.amount})</span>
													</label>
												))}
											</div>
										</Popover.Dropdown>
									</Popover>
								</Stack>
								<Stack spacing={6} className={styles.sortControl}>
									<Text size="sm" fw={700}>Sort</Text>
									<Select {...FILTER_SELECT_PROPS} value={sortValue} data={SORT_OPTIONS} onChange={(value) => {
										setSortValue(value || DEFAULT_SORT_VALUE)
										resetPage()
									}} />
								</Stack>
								<div className={styles.refreshControl}>
									<Button
										variant="default"
										leftIcon={<IconRefresh size={16} />}
										loading={loading}
										onClick={refreshPage}
										className={styles.refreshButton}
									>
										Refresh
									</Button>
								</div>
							</Flex>
						</Card>

						<SimpleGrid cols={4} spacing="sm" breakpoints={[{ maxWidth: 1200, cols: 2 }, { maxWidth: 720, cols: 1 }]}>
							{overviewStatItems.map((item) => {
								const Icon = item.icon
								return (
									<Card key={item.label} withBorder radius="md" p="lg" className={styles.overviewStatCard}>
										<Flex align="center" justify="space-between" gap={12}>
											<Stack spacing={2}>
												<Text size="sm" color="dimmed">{item.label}</Text>
												<Text size={26} fw={800}>{item.value}</Text>
											</Stack>
											<div className={styles.overviewStatIcon}><Icon size={22} /></div>
										</Flex>
									</Card>
								)
							})}
						</SimpleGrid>

						<Card withBorder radius="md" p="md" className={styles.serviceStatsPanel}>
							<Flex align="center" justify="space-between" gap={12} wrap="wrap" mb={12}>
								<Stack spacing={2}>
									<Text size="xs" fw={800} color="green" tt="uppercase">
										Service Breakdown
									</Text>
									<Text size="sm" color="dimmed">
										Breakdown by service type, separated from the overview metrics above.
									</Text>
								</Stack>
							</Flex>
							<SimpleGrid cols={4} spacing="sm" breakpoints={[{ maxWidth: 1200, cols: 2 }, { maxWidth: 720, cols: 1 }]}>
								{serviceStatItems.map((item) => {
									const Icon = item.icon
									return (
										<div key={item.label} className={styles.serviceStatCard}>
											<div className={styles.serviceStatIcon}><Icon size={18} /></div>
											<Stack spacing={0}>
												<Text size="xs" color="dimmed" fw={700}>{item.label}</Text>
												<Text size={22} fw={800} color="#087a28">{item.value}</Text>
											</Stack>
										</div>
									)
								})}
							</SimpleGrid>
						</Card>

						<div className={styles.tableWrap}>
							<TableComponent
								columns={columns}
								total={total}
								data={rows}
								pagination={pagination}
								loading={loading}
								handleChangePage={(pageIndex: number) => setPagination((previous) => ({ ...previous, pageIndex }))}
								handleChangePageSize={(pageSize: number) => setPagination({ pageIndex: 1, pageSize })}
								searchQueryValue={searchKey}
								onSearchQueryInit={setSearchKey}
								filterQueryValues={{
									sort: sortValue !== DEFAULT_SORT_VALUE ? sortValue : undefined,
									service_type: serviceType || undefined,
									is_show_by_admin: statusFilter || undefined,
									status: undefined,
									category_id: categoryIds.length ? categoryIds.join(',') : undefined,
								}}
								onFilterQueryInit={(query: Record<string, string>) => {
									setSortValue(query.sort || DEFAULT_SORT_VALUE)
									setServiceType(query.service_type || '')
									setStatusFilter(
										getStatusFilterValue(
											query.is_show_by_admin || query.status,
										),
									)
									setCategoryIds(query.category_id ? query.category_id.split(',').filter(Boolean) : [])
								}}
								_renderCustomActions={renderCustomActions}
								actionColumnSize={130}
								enableRowActions
								lockActionColumn
								compact
							/>
						</div>
					</Stack>
				</Tabs.Panel>

				<Tabs.Panel value="deleted-profiles" pt="md">
					<Stack spacing={16}>
						<Card withBorder radius="md" p="md" className={styles.headerCard}>
							<Flex gap={10} align="flex-end" wrap="wrap">
								<Stack spacing={6} className={styles.searchControl}>
									<Text size="sm" fw={700}>Search</Text>
									<TextInput
										value={deletedProfileSearch}
										onChange={(event) => {
											setDeletedProfileSearch(event.currentTarget.value)
											setDeletedProfilePagination((previous) => ({ ...previous, pageIndex: 1 }))
										}}
										placeholder="Search profile ID, name, email or phone"
										icon={<IconSearch size={16} />}
									/>
								</Stack>
								<Button
									variant="default"
									leftIcon={<IconRefresh size={16} />}
									onClick={refreshPage}
									loading={loading}
								>
									Refresh
								</Button>
							</Flex>
						</Card>
						<div className={styles.tableWrap}>
							<TableComponent
								columns={deletedProfileColumns}
								total={deletedProfileTotal}
								data={deletedProfiles}
								pagination={deletedProfilePagination}
								loading={loading}
								handleChangePage={(pageIndex: number) => setDeletedProfilePagination((previous) => ({ ...previous, pageIndex }))}
								handleChangePageSize={(pageSize: number) => setDeletedProfilePagination({ pageIndex: 1, pageSize })}
								searchQueryKey="deleted_profile_search"
								searchQueryValue={deletedProfileSearch}
								onSearchQueryInit={setDeletedProfileSearch}
								_renderCustomActions={(row: any) => {
									const profile = row.original as DeletedGuideProfile
									const loadingRow = actionLoadingId === profile.id
									return (
										<CustomTooltip withinPortal label="Restore profile">
											<button
												type="button"
												onClick={() => handleRestoreDeletedProfile(profile)}
												disabled={Boolean(actionLoadingId)}
												className={`${actionClasses.actionButton} ${actionClasses.primaryAction} ${loadingRow ? styles.actionLoading : ''}`}
											>
												<IconRestore size={16} />
											</button>
										</CustomTooltip>
									)
								}}
								actionColumnSize={96}
								enableRowActions
								hideMoreActions
								lockActionColumn
								compact
							/>
						</div>
					</Stack>
				</Tabs.Panel>

				<Tabs.Panel value="categories" pt="md">
					<Stack spacing={16}>
						<Card withBorder radius="md" p="md" className={styles.headerCard}>
							<TextInput
								value={categorySearch}
								onChange={(event) => {
									setCategorySearch(event.currentTarget.value)
									setCategoryPagination((previous) => ({ ...previous, pageIndex: 1 }))
								}}
								placeholder="Search category"
								icon={<IconSearch size={16} />}
								className={styles.categorySearch}
							/>
						</Card>
						<div className={styles.tableWrap}>
							<TableComponent
								columns={categoryColumns}
								total={categoryTotal}
								data={categories}
								pagination={categoryPagination}
								loading={loading}
								handleChangePage={(pageIndex: number) => setCategoryPagination((previous) => ({ ...previous, pageIndex }))}
								handleChangePageSize={(pageSize: number) => setCategoryPagination({ pageIndex: 1, pageSize })}
								searchQueryKey="category_search"
								searchQueryValue={categorySearch}
								onSearchQueryInit={setCategorySearch}
								enableRowActions={false}
								compact
							/>
						</div>
					</Stack>
				</Tabs.Panel>
			</Tabs>

			<Modal opened={Boolean(selectedListing)} onClose={() => setSelectedListing(null)} title="FMG Listing Detail" size="xl">
				{selectedListing && (() => {
					const detail = selectedListing.detail || {}
					const detailCategories = detail.categories?.length
						? detail.categories
						: selectedListing.category
						? [{ title: selectedListing.category }]
						: []
					const languages = joinText(
						(detail.languages || []).map((language: any) =>
							language.name || language.native_name || language.code,
						),
					)
					const categoriesText = joinText(
						detailCategories.map((category: any) => category.title || category.name),
					)
					const pickupText = joinText(
						(detail.pickup_details || []).map((pickup: any) =>
							[pickup.pickup_time, pickup.pickup_name, getLocationAddress(pickup.location)]
								.filter(Boolean)
								.join(', '),
						),
					)
					const itineraryItems = detail.itinerary || []
					const cancelPolicyText = joinText(
						(detail.cancel_policies || []).map((policy: any) =>
							[
								policy.cancel_before_days ? `${policy.cancel_before_days} days` : '',
								policy.charge_rate !== undefined && policy.charge_rate !== null ? `${policy.charge_rate}%` : '',
								policy.note,
							].filter(Boolean).join(' - '),
						),
					)

					return (
						<Stack spacing="md">
							<DetailSection title="Basic info">
								<Group spacing={12} noWrap align="flex-start">
									<Avatar src={selectedListing.image || undefined} radius="md" size={82}>
										{selectedListing.title.charAt(0)}
									</Avatar>
									<Stack spacing={5} className={styles.detailHeaderContent}>
										<Text fw={800} size="lg">{selectedListing.title}</Text>
										<Group spacing={6}>
											<Badge color="green" variant="light" sx={{ textTransform: 'none' }}>
												{selectedListing.serviceTypeLabel || '-'}
											</Badge>
											<Badge color={selectedListing.status === 'active' ? 'green' : selectedListing.status === 'draft' ? 'yellow' : 'gray'} variant="outline" sx={{ textTransform: 'none' }}>
												Status by user: {selectedListing.status || '-'}
											</Badge>
											<Badge color={selectedListing.isShowByAdmin ? 'green' : 'gray'} variant="outline" sx={{ textTransform: 'none' }}>
												Status by admin: {selectedListing.isShowByAdmin ? 'Active' : 'Inactive'}
											</Badge>
										</Group>
										<DetailRow label="Seller" value={selectedListing.seller?.name || '-'} />
										<DetailRow label="Seller email" value={selectedListing.seller?.email || '-'} />
										<DetailRow label="Categories" value={categoriesText} />
										<DetailRow label="Location" value={selectedListing.location || getLocationAddress(detail.business_location)} />
										{selectedListing.shareLink && (
											<Button
												variant="default"
												leftIcon={<IconCopy size={16} />}
												onClick={() => handleCopy(selectedListing.shareLink, 'Copied share link')}
												className={styles.detailCopyButton}
											>
												Copy share link
											</Button>
										)}
									</Stack>
								</Group>
							</DetailSection>

							{selectedListing.description && (
								<DetailSection title="Introduction">
									<Text size="sm" className={styles.detailParagraph}>{selectedListing.description}</Text>
								</DetailSection>
							)}

							<DetailSection title="Overview">
								<DetailRow label="Languages" value={languages} />
								<DetailRow label="Schedule" value={formatSchedule(detail.schedule)} />
								<DetailRow
									label="Hotel pickup"
									value={typeof detail.hotel_pickup === 'boolean' ? formatBooleanAvailability(detail.hotel_pickup) : undefined}
								/>
								<DetailRow
									label="Meeting point pickup"
									value={typeof detail.meeting_point_pickup === 'boolean' ? formatBooleanAvailability(detail.meeting_point_pickup) : undefined}
								/>
								<DetailRow label="Pickup details" value={pickupText} />
								{itineraryItems.length > 0 && (
									<Stack spacing={6}>
										<Text size="sm" color="dimmed" fw={700}>Itinerary</Text>
										{itineraryItems.map((day: any) => (
											<div key={day.id || day.day_number} className={styles.itineraryBlock}>
												<Text size="sm" fw={700}>
													Day {day.day_number}{day.day_name ? ` - ${day.day_name}` : ''}
												</Text>
												{(day.stops || []).map((stop: any) => (
													<Text key={stop.id || stop.stop_name} size="sm" color="dimmed">
														{[stop.stop_name, getLocationAddress(stop.location), stop.stop_duration].filter(Boolean).join(' | ')}
													</Text>
												))}
											</div>
										))}
									</Stack>
								)}
							</DetailSection>

							<DetailSection title="Before you book">
								<DetailRow label="Max participants" value={detail.max_participants} />
								<DetailRow label="Spots available" value={detail.spots_available} />
								<DetailRow label="Categories" value={categoriesText} />
								<DetailRow label="What to bring" value={detail.what_to_bring} />
								<DetailRow label="What's included" value={detail.what_included} />
								<DetailRow label="Children & pets policy" value={detail.child_pet_policy} />
								<DetailRow
									label="Insurance"
									value={typeof detail.insurance === 'boolean' ? formatBooleanAvailability(detail.insurance) : undefined}
								/>
								<DetailRow label="Insurance note" value={detail.insurance_note} />
								<DetailRow
									label="Business license"
									value={typeof detail.business_license === 'boolean' ? formatBooleanAvailability(detail.business_license) : undefined}
								/>
								<DetailRow label="Business license note" value={detail.business_license_note} />
								<DetailRow label="Business location" value={getLocationAddress(detail.business_location)} />
								<DetailRow label="Cancel policy note" value={detail.cancel_policy_note} />
								<DetailRow label="Cancel policies" value={cancelPolicyText} />
							</DetailSection>

							<DetailSection title="Pricing / Rating">
								<DetailRow label="Price" value={formatVndPrice(selectedListing.price)} />
								<Group spacing={4} noWrap>
									<Text size="sm" color="dimmed" fw={700}>Rating</Text>
									<IconStarFilled size={14} color="#f7b500" />
									<Text size="sm">{selectedListing.rating || '-'} ({selectedListing.ratingCount})</Text>
								</Group>
								{(detail.price_details || []).length > 0 && (
									<>
										<Divider />
										<Stack spacing={6}>
											<Text size="sm" color="dimmed" fw={700}>Price details</Text>
											{detail.price_details.map((price: any, index: number) => (
												<Text key={price.id || index} size="sm">
													{[
														price.price_type,
														price.price !== undefined && price.price !== null ? formatVndPrice(price.price) : '',
														price.price_duration,
														price.note,
													].filter(Boolean).join(' | ')}
												</Text>
											))}
										</Stack>
									</>
								)}
							</DetailSection>
						</Stack>
					)
				})()}
			</Modal>
		</Stack>
	)
}

export default FindMyGuidePage
